// dashboard/frontend/src/pages/Home.js
import React, { useState } from 'react';
import {
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Box,
    Button,
    Card,
    Container,
    Grid,
    Typography,
    useTheme,
    List,
    ListItem,
    ListItemIcon,
    ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    Timeline,
    TimelineItem,
    TimelineContent
} from '@mui/lab';
import { Box as IllustrationBox } from '@mui/material';
import { CheckCircleOutline } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import PricingDialog from '../components/Pricing/PricingDialog';

const Home = () => {
    const [showPricing, setShowPricing] = useState(false);
    const theme = useTheme();
    const navigate = useNavigate();
    const { user } = useAuth();

    const features = [
        {
            title: "Professional Setup",
            description: "5 Warmed-up Sending Domains • Google Workspace Integration • Full DNS Configuration",
            icon: "🚀"
        },
        {
            title: "AI-Powered Personalization",
            description: "Advanced Email Generation • Custom Writing Style Matching • Smart Scheduling",
            icon: "🤖"
        },
        {
            title: "Campaign Management",
            description: "Deliverability Optimization • Performance Tracking • Expert Support",
            icon: "📈"
        }
    ];

    const solutions = [
      {
          title: "AI-Powered Personalization",
          description: "Our advanced AI analyzes your prospect's online presence to create truly personalized messages that resonate.",
          details: [
              "LinkedIn profile analysis",
              "Company website content integration",
              "Industry-specific messaging",
              "Personal tone matching"
          ],
          icon: "🧠"
      },
      {
          title: "Managed Infrastructure",
          description: "Focus on your business while we handle the complex email infrastructure setup and maintenance.",
          details: [
              "Domain warming strategies",
              "SPF, DKIM, DMARC setup",
              "Reputation monitoring",
              "Deliverability optimization"
          ],
          icon: "⚙️"
      },
      {
          title: "Campaign Optimization",
          description: "Data-driven insights to continuously improve your campaign performance.",
          details: [
              "A/B testing",
              "Send time optimization",
              "Response rate analytics",
              "Performance benchmarking"
          ],
          icon: "📊"
      }
    ];

    const faqs = [
      {
          question: "How does the AI personalization work?",
          answer: "Our AI analyzes your prospect's public information from sources like LinkedIn and company websites to create genuinely personalized messages. It adapts to your writing style and maintains consistency across all communications."
      },
      {
          question: "What's included in the email infrastructure setup?",
          answer: "We handle everything: domain registration, DNS configuration (SPF, DKIM, DMARC), email provider setup, and systematic warm-up of your sending domains to ensure optimal deliverability."
      },
      {
          question: "How long does it take to get started?",
          answer: "Initial setup takes 1-2 business days. The warm-up phase typically runs for 4-6 weeks to establish strong sender reputation. However, you can start customizing your campaigns immediately after setup."
      },
      {
          question: "Can I use my existing email accounts?",
          answer: "Yes! Our BYOE (Bring Your Own Email) plan allows you to connect your existing warmed-up Gmail accounts. We'll verify their current status and help optimize their configuration."
      },
      {
          question: "What kind of support do you provide?",
          answer: "All plans include technical support. Professional and Enterprise plans include priority support with response times under 4 hours and dedicated account management."
      }
    ];

    const handleGetStarted = () => {
        if (user) {
            navigate('/dashboard');
        } else {
            setShowPricing(true);
        }
    };

    return (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                pb: 8  // Add padding bottom to ensure space before footer
            }}
        >
            <Box sx={{ flex: 1 }}>  {/* This box will take up available space */}
                <Container maxWidth="lg">
                    {/* Hero Section */}
                    <Box
                        sx={{
                            background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                            color: 'white',
                            py: { xs: 8, md: 12 },
                            mb: 6,
                            borderRadius: { xs: 0, md: 2 },
                            textAlign: 'center'
                        }}
                    >
                        <Container maxWidth="md">
                            <Typography 
                                variant="h2" 
                                gutterBottom
                                sx={{ 
                                    fontWeight: 700,
                                    fontSize: { xs: '2.5rem', md: '3.75rem' }
                                }}
                            >
                                Personalized Cold Outreach That Actually Works
                            </Typography>
                            <Typography 
                                variant="h5" 
                                sx={{ mb: 4, opacity: 0.9 }}
                            >
                                AI-Powered Personalization at Scale • Managed Infrastructure • Expert Support
                            </Typography>
                            <Button
                                variant="contained"
                                size="large"
                                onClick={handleGetStarted}
                                sx={{
                                    backgroundColor: 'white',
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        backgroundColor: '#f5f5f5'
                                    }
                                }}
                            >
                                Get Started
                            </Button>
                        </Container>
                    </Box>

                    {/* Features Section */}
                    <Grid container spacing={4}>
                        {features.map((feature) => (
                            <Grid item xs={12} md={4} key={feature.title}>
                                <Card 
                                    elevation={2}
                                    sx={{ 
                                        height: '100%', 
                                        p: 3,
                                        transition: '0.3s',
                                        '&:hover': {
                                            transform: 'translateY(-4px)',
                                            boxShadow: 4
                                        }
                                    }}
                                >
                                    <Typography 
                                        variant="h1" 
                                        sx={{ mb: 2, fontSize: '3rem' }}
                                    >
                                        {feature.icon}
                                    </Typography>
                                    <Typography variant="h5" gutterBottom>
                                        {feature.title}
                                    </Typography>
                                    <Typography color="text.secondary">
                                        {feature.description}
                                    </Typography>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </Container>
            </Box>

            {/* How It Works Section */}
            <Box sx={{ py: 8, backgroundColor: 'grey.50' }}>
                <Container maxWidth="lg">
                    <Typography variant="h3" align="center" gutterBottom>
                        How It Works
                    </Typography>
                    <Typography variant="h6" align="center" color="text.secondary" sx={{ mb: 6 }}>
                        Professional email outreach, simplified
                    </Typography>
                    
                    {/* Add step-by-step process */}
                    <Timeline position="alternate">
                        <TimelineItem>
                            <TimelineContent>
                                <Typography variant="h6" color="primary">1. Initial Setup</Typography>
                                <Typography>Professional domain registration and email infrastructure configuration</Typography>
                                <Box sx={{ mt: 1, color: 'text.secondary' }}>
                                    <Typography variant="body2">• Domain registration and verification</Typography>
                                    <Typography variant="body2">• SPF, DKIM, DMARC setup</Typography>
                                    <Typography variant="body2">• Email provider configuration</Typography>
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                        <TimelineItem>
                            <TimelineContent>
                                <Typography variant="h6" color="primary">2. Warm-up Phase</Typography>
                                <Typography>Systematic warm-up of your email accounts</Typography>
                                <Box sx={{ mt: 1, color: 'text.secondary' }}>
                                    <Typography variant="body2">• Gradual sending volume increase</Typography>
                                    <Typography variant="body2">• Reputation building</Typography>
                                    <Typography variant="body2">• Delivery monitoring</Typography>
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                        <TimelineItem>
                            <TimelineContent>
                                <Typography variant="h6" color="primary">3. Campaign Setup</Typography>
                                <Typography>Configure your personalization preferences and AI settings</Typography>
                                <Box sx={{ mt: 1, color: 'text.secondary' }}>
                                    <Typography variant="body2">• Writing style customization</Typography>
                                    <Typography variant="body2">• Target audience definition</Typography>
                                    <Typography variant="body2">• Response handling setup</Typography>
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                        <TimelineItem>
                            <TimelineContent>
                                <Typography variant="h6" color="primary">4. Active Campaign</Typography>
                                <Typography>Launch and monitor your personalized outreach</Typography>
                                <Box sx={{ mt: 1, color: 'text.secondary' }}>
                                    <Typography variant="body2">• AI-powered message generation</Typography>
                                    <Typography variant="body2">• Real-time performance tracking</Typography>
                                    <Typography variant="body2">• Continuous optimization</Typography>
                                </Box>
                            </TimelineContent>
                        </TimelineItem>
                    </Timeline>
                </Container>
            </Box>

            {/* Detailed Features Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    {solutions.map((solution, index) => (
                        <Grid container spacing={4} sx={{ mb: 8 }} alignItems="center">
                            <Grid item xs={12} md={6} order={{ xs: 1, md: index % 2 }}>
                                <Typography variant="h4" gutterBottom>
                                    {solution.icon} {solution.title}
                                </Typography>
                                <Typography variant="h6" paragraph>
                                    {solution.description}
                                </Typography>
                                <List>
                                    {solution.details.map((detail) => (
                                        <ListItem>
                                            <ListItemIcon>
                                                <CheckCircleOutline color="primary" />
                                            </ListItemIcon>
                                            <ListItemText primary={detail} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Grid>
                            <Grid item xs={12} md={6} order={{ xs: 2, md: index % 2 === 0 ? 2 : 1 }}>
                                <IllustrationBox
                                    sx={{
                                        height: 300,
                                        backgroundColor: 'grey.100',
                                        borderRadius: 2,
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontSize: '4rem'
                                    }}
                                >
                                    {solution.icon}
                                </IllustrationBox>

                            </Grid>
                        </Grid>
                    ))}
                </Container>
            </Box>

            {/* Social Proof Section */}
            <Box sx={{ py: 8, backgroundColor: 'grey.50' }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" align="center" gutterBottom>
                        Trusted by Growing Businesses
                    </Typography>
                    <Grid container spacing={4} sx={{ mt: 4 }}>
                      {[1, 2, 3].map((item) => (
                          <Grid item xs={12} md={4} key={item}>
                              <Card sx={{ height: '100%', p: 3 }}>
                                  <Typography sx={{ mb: 2 }}>⭐⭐⭐⭐⭐</Typography>
                                  <Typography paragraph>
                                      "The AI personalization is incredible. Our response rates increased by 3x within the first month."
                                  </Typography>
                                  <Typography variant="subtitle2" color="primary">
                                      Mike D.
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary">
                                      Sales Director
                                  </Typography>
                              </Card>
                          </Grid>
                      ))}
                  </Grid>
                </Container>
            </Box>

            {/* FAQ Section */}
            <Box sx={{ py: 8 }}>
                <Container maxWidth="lg">
                    <Typography variant="h4" align="center" gutterBottom>
                        Frequently Asked Questions
                    </Typography>
                    {faqs.map((faq, index) => (
                        <Accordion key={index}>
                            <AccordionSummary
                                expandIcon={<ExpandMoreIcon />}
                                aria-controls={`panel${index}-content`}
                                id={`panel${index}-header`}
                            >
                                <Typography variant="h6">{faq.question}</Typography>
                            </AccordionSummary>
                            <AccordionDetails>
                                <Typography>{faq.answer}</Typography>
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </Container>
            </Box>


            {/* Call to Action Section */}
            <Box 
                sx={{ 
                    py: 8, 
                    background: `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.primary.main} 90%)`,
                    color: 'white'
                }}
            >
                <Container maxWidth="md" sx={{ textAlign: 'center' }}>
                    <Typography variant="h4" gutterBottom>
                        Ready to Grow Your Business?
                    </Typography>
                    <Typography variant="h6" sx={{ mb: 4 }}>
                        Join our early adopters program and get 50% off forever!
                    </Typography>
                    <Button
                        variant="contained"
                        size="large"
                        onClick={handleGetStarted}
                        sx={{
                            backgroundColor: 'white',
                            color: theme.palette.primary.main,
                            '&:hover': {
                                backgroundColor: '#f5f5f5'
                            }
                        }}
                    >
                        Get Started Now
                    </Button>
                </Container>
            </Box>


            {/* Pricing Dialog */}
            <PricingDialog 
                open={showPricing} 
                onClose={() => setShowPricing(false)} 
            />
        </Box>
    );
};

export default Home;